import './DynamicPlansSection.scss';

import { BlockCta } from '@travelwin/cms/types';
import { Body, ButtonV2, ProductOffering, Title } from '@travelwin/core';
import { EmblaOptionsType } from 'embla-carousel';
import { ReactNode } from 'react';

import { ACTION_VIA_SOURCE_TYPE, useAnalytics } from '@/analytics';
import { useMediaQuery } from '@/hooks';
import { useExternalHomepageConfig } from '@/hooks/useExternalHomepageConfig';
import EmblaCarousel from '@/pages/Landing/components/Carousel/EmblaCarousel';

import { Link } from '../Link';
import ClickablePlanCardContainer from '../PlanCard/ClickablePlanCardContainer';

const className = 'c-DynamicPlansSection';

interface DynamicPlansSectionProps {
  useTitle?: boolean | null;
  title?: ReactNode;
  subtitle?: ReactNode;
  plans: Array<ProductOffering>;
  cta?: BlockCta;
  showCarouselOnMobile?: boolean | null;
  carouselOptions?: EmblaOptionsType;
}

const DynamicPlansSection = ({
  useTitle = false,
  title,
  subtitle,
  plans = [],
  cta,
  showCarouselOnMobile = true,
  carouselOptions,
}: DynamicPlansSectionProps) => {
  const analyticsContext = useAnalytics();
  const { getLocalisedUrl } = useExternalHomepageConfig();
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 991px)');

  if (plans.length === 0) {
    return null;
  }

  const planCards = plans.map((plan, index) =>
    plan ? (
      <div key={`${plan.id}-${index}`} className={`${className}__card`}>
        <ClickablePlanCardContainer
          itemIndex={0}
          planDetails={plan}
          actionViaSource={ACTION_VIA_SOURCE_TYPE.BLOCK}
        />
      </div>
    ) : null,
  );

  const onCtaTracking = (title: string) => {
    analyticsContext?.trackClickAction({
      element_id: 'cta',
      element_text: title,
    });
  };

  return (
    <div className={className}>
      {useTitle && (
        <div className={`${className}__header`}>
          {title && (
            <Title
              className={`${className}__header-title`}
              tag={2}
              level={{
                sm: 3,
                md: 3,
                lg: 2,
              }}
            >
              {title}
            </Title>
          )}

          {subtitle && (
            <Body size={16} className={`${className}__header-subtitle`}>
              {subtitle}
            </Body>
          )}
        </div>
      )}

      <div className={`${className}__cards ${className}__cards--tablet-down`}>
        {showCarouselOnMobile ? (
          <EmblaCarousel
            options={{
              slidesToScroll: isTablet ? 2 : 1,
              align: 'center',
              loop: true,
              ...carouselOptions,
            }}
          >
            {planCards}
          </EmblaCarousel>
        ) : (
          planCards
        )}
      </div>

      <div className={`${className}__cards ${className}__cards--desktop`}>
        {planCards}
      </div>

      {cta && cta?.href && (
        <div className={`${className}__cta`}>
          <ButtonV2
            data-testid="landing-page-cta-button"
            variant="secondary"
            size={'medium'}
            className={`${className}__cta-button`}
            asChild
            fluid
            key="landing-page-cta-button"
            onClick={() => cta?.title && onCtaTracking(cta.title)}
          >
            {cta.href.startsWith('/') ? (
              <Link to={cta.href} target={cta.newTab ? '_blank' : undefined}>
                {cta.title}
              </Link>
            ) : (
              <a
                data-testid="landing-page-cta-button"
                href={getLocalisedUrl(cta.href)}
                target={cta.newTab ? '_blank' : undefined}
                onClick={() => cta?.title && onCtaTracking(cta.title)}
              >
                {cta.title}
              </a>
            )}
          </ButtonV2>
        </div>
      )}
    </div>
  );
};

export default DynamicPlansSection;
